import React, { createContext, useContext, useState, useCallback } from 'react';
import { useFeatureContext } from 'contexts/FeatureContext.jsx';
import useModal from 'hooks/useModal.js';

export const DrawerContext = createContext({
  scrollDrawerContent: true,
});

export const useDrawerContext = () => useContext(DrawerContext);

export const DrawerContextProvider = ({ children }) => {
  const { features: { productsNavigationEnabled } } = useFeatureContext();
  const { isOpen: drawerIsOpen, open: openDrawerInState, close: closeDrawerInState } = useModal();
  const [isCloseOnOverlayActive, setIsCloseOnOverlayActive] = useState(true);
  const [drawerPages, setDrawerPages] = useState([]);
  const [page, setPage] = useState(0);
  const [drawerContext, setDrawerContext] = useState(null);
  const [scrollDrawerContent, setScrollDrawerContent] = useState(true);

  const openDrawer = useCallback((options) => {
    const { drawerPages, isCloseOnOverlayActive = true, context } = options || {};
    setDrawerPages(drawerPages);
    setPage(prevPage => drawerPages.length > prevPage ? prevPage : 0);
    openDrawerInState();
    setIsCloseOnOverlayActive(isCloseOnOverlayActive);
    setDrawerContext(context);
  }, [openDrawerInState]);

  const onClose = drawerPages[page]?.onClose;
  const closeDrawer = useCallback(() => {
    closeDrawerInState();
    if (typeof onClose !== 'function') return;
    onClose();
    setPage(0);
  }, [closeDrawerInState, onClose]);

  const hasDrawerPages = drawerPages?.length > 0;
  const removeDrawer = useCallback(() => {
    if (!hasDrawerPages) return;
    if (drawerIsOpen) closeDrawer();
    setPage(0);
    setDrawerPages([]);
    setDrawerContext(null);
    setScrollDrawerContent(true);
    // At this point we should no longer have any drawers to present,
    // hence purging this value seems to make sense.
  }, [hasDrawerPages, drawerIsOpen, closeDrawer]);

  const setPageAndUpdateState = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  // To keep old dashboard UI keep using MUI Drawer we just return children
  if (!productsNavigationEnabled) return children;

  const context = {
    drawerPages,
    drawerContext,
    setDrawerPages,
    setDrawerContext,
    page,
    setPage: setPageAndUpdateState,
    drawerIsOpen,
    openDrawerInState,
    closeDrawerInState,
    removeDrawer,
    openDrawer,
    closeDrawer,
    isCloseOnOverlayActive,
    scrollDrawerContent,
    setScrollDrawerContent,
  };
  return (
    <DrawerContext.Provider value={context}>
      {children}
    </DrawerContext.Provider>
  );
};
